import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Loader2, Send, Download, X, MailX } from "lucide-react";

const TIER_STYLES = {
  "Highly Engaged": "bg-emerald-100 text-emerald-800",
  Engaged: "bg-sky-100 text-sky-800",
  "At Risk": "bg-amber-100 text-amber-800",
  Dormant: "bg-slate-200 text-slate-700",
};

function SendMessageModal({ row, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [commId, setCommId] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/comm-templates")
      .then((r) => { const t = r.data.templates || []; setTemplates(t); if (t.length) setCommId(t[0].id); })
      .catch(() => setTemplates([]));
  }, []);

  const send = async () => {
    if (!commId) return;
    setSending(true);
    try {
      const r = await api.post(`/alumni/${row.id}/send-communication`, { comm_id: commId });
      toast.success(r.data.message || "Message sent");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" data-testid="send-message-modal" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-serif text-lg tracking-tight flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Send a message</h3>
          <button data-testid="send-modal-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="text-sm">
            <div className="text-muted-foreground">Re-engaging</div>
            <div className="font-medium">{row.name} <span className="text-muted-foreground">· Class of {row.class_year ?? "—"}</span></div>
            <div className="text-muted-foreground text-xs mt-0.5">{row.email}</div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Template</label>
            {templates === null ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="text-sm text-muted-foreground py-3">No sendable templates found. Create one on the Communications page first.</div>
            ) : (
              <select data-testid="send-template-select" value={commId} onChange={(e) => setCommId(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="send-message-confirm" onClick={send} disabled={sending || !commId || !templates?.length} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EngagementLeaderboard() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(50);
  const [classYear, setClassYear] = useState("");
  const [years, setYears] = useState([]);
  const [sendRow, setSendRow] = useState(null);

  useEffect(() => {
    api.get("/alumni/filters").then((r) => setYears(r.data.class_years || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null);
    const cy = classYear ? `&class_year=${classYear}` : "";
    api.get(`/engagement/leaderboard?limit=${limit}${cy}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ leaderboard: [], total: 0, distribution: {} }));
  }, [limit, classYear]);

  const exportCsv = () => {
    if (!data?.leaderboard?.length) return;
    const headers = ["Rank", "Name", "Class Year", "Tier", "Score", "Gifts", "Events", "Opens", "Activity", "City", "State", "Email", "Claimed"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = data.leaderboard.map((r, i) => [
      i + 1, r.name, r.class_year ?? "", r.engagement.tier, r.engagement.score,
      r.engagement.gifts, r.engagement.events, r.engagement.opens, r.engagement.activity,
      r.city, r.state, r.email || "", r.claimed ? "Yes" : "No",
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `engagement-leaderboard${classYear ? `-class-${classYear}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fade-up" data-testid="engagement-leaderboard">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1 flex items-center gap-2"><TrendingUp className="h-7 w-7 text-primary" /> Engagement Leaderboard</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Your most-engaged alumni, ranked by giving, event attendance, email opens and activity. Prioritize outreach to the top of the list and re-engage those at risk.</p>
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {["Highly Engaged", "Engaged", "At Risk", "Dormant"].map((t) => (
              <div key={t} data-testid={`dist-${t.replace(/\s/g, "-").toLowerCase()}`} className="bg-card border border-border rounded-lg p-4">
                <div className={`inline-block text-[10px] font-mono uppercase px-2 py-0.5 rounded ${TIER_STYLES[t]}`}>{t}</div>
                <div className="text-2xl font-serif mt-2 tabular-nums">{data.distribution?.[t] || 0}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-sm text-muted-foreground">{data.total} living alumni scored{classYear ? ` in Class of ${classYear}` : ""} · showing top {data.leaderboard.length}</div>
            <div className="flex items-center gap-2">
              <select data-testid="class-year-filter" value={classYear} onChange={(e) => setClassYear(e.target.value)} className="px-3 py-1.5 rounded-md border border-border bg-background text-sm">
                <option value="">All classes</option>
                {years.map((y) => <option key={y} value={y}>Class of {y}</option>)}
              </select>
              <select data-testid="limit-select" value={limit} onChange={(e) => setLimit(parseInt(e.target.value))} className="px-3 py-1.5 rounded-md border border-border bg-background text-sm">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>Top {n}</option>)}
              </select>
              <button data-testid="export-csv-btn" onClick={exportCsv} disabled={!data.leaderboard.length} className="px-3 py-1.5 rounded-md border border-border bg-background text-sm hover:bg-muted disabled:opacity-50 flex items-center gap-1.5"><Download className="h-4 w-4" /> Export CSV</button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-2.5 w-12">#</th>
                  <th className="px-4 py-2.5">Alumnus</th>
                  <th className="px-4 py-2.5">Class</th>
                  <th className="px-4 py-2.5">Engagement</th>
                  <th className="px-4 py-2.5 hidden md:table-cell">Location</th>
                  <th className="px-4 py-2.5 hidden lg:table-cell">Contact</th>
                  <th className="px-4 py-2.5 text-right">Outreach</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((r, i) => {
                  const canSend = !!r.email;
                  return (
                    <tr key={r.id} data-testid={`leader-row-${r.id}`} className="border-t border-border hover:bg-muted/40">
                      <td className="px-4 py-2.5 tabular-nums text-muted-foreground">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <Link to={`/alumni/${r.id}`} className="font-medium text-primary hover:underline">{r.name || "—"}</Link>
                        {!r.claimed && <span className="ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Unclaimed</span>}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{r.class_year ?? "—"}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${TIER_STYLES[r.engagement.tier]}`}>{r.engagement.tier}</span>
                          <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block"><div className="h-full bg-primary" style={{ width: `${r.engagement.score}%` }} /></div>
                          <span className="tabular-nums text-xs text-muted-foreground">{r.engagement.score}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-muted-foreground truncate max-w-[220px]">{r.email || "No email"}</td>
                      <td className="px-4 py-2.5 text-right">
                        {canSend ? (
                          <button data-testid={`send-message-btn-${r.id}`} onClick={() => setSendRow(r)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                            <Send className="h-3.5 w-3.5" /> Send a message
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70"><MailX className="h-3.5 w-3.5" /> No email</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.leaderboard.length === 0 && <div className="text-center text-muted-foreground text-sm py-10">No alumni to show.</div>}
          </div>
        </>
      )}

      {sendRow && <SendMessageModal row={sendRow} onClose={() => setSendRow(null)} />}
    </div>
  );
}
